import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural, pluralizeIssues } from '../../../../shared/helpers/plural';
import { useAddToast } from '../../../contexts/ToastContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { ignoreOrResolveWord, ignoreOrResolveWordPast } from '../../../utils';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import InlineLoaderButton from '../../generic/Button/InlineLoaderButton';
import { ErrorAlert } from '../../generic/ErrorAlert';
import RouterLink from '../../generic/Link/RouterLink';

import useClearErrorsOnUnmount from './hooks/useClearErrorsOnUnmount';

type UnignoreIssuesModalBaseProps = {
  show: boolean;
  selectedIssuesCount: number;
  onHide: () => void;
  areAnyIgnoredByRule?: boolean;
};

type UnignoreIssuesModalProps = UnignoreIssuesModalBaseProps & {
  onUnignore: () => void;
};

const UnignoreExceptionIssueWarning: VoidFunctionComponent<
  Pick<UnignoreIssuesModalBaseProps, 'areAnyIgnoredByRule' | 'selectedIssuesCount'>
> = ({ areAnyIgnoredByRule, selectedIssuesCount }) => {
  if (!areAnyIgnoredByRule) {
    return null;
  }

  const numIssuesMessage = selectedIssuesCount === 1 ? 'this issue is' : 'one or more issues are';

  const resolvedOrIgnored = ignoreOrResolveWordPast().toLowerCase();

  return (
    <div>
      <b>Warning:</b> Because {numIssuesMessage} {resolvedOrIgnored} by a rule,{' '}
      {plural(selectedIssuesCount, 'it', 'they')}{' '}
      can return to {resolvedOrIgnored} on the next issue scan. To prevent this,{' '}
      <u>
        <RouterLink to={`${window.location.pathname}/exceptions`}>remove the rule(s)</RouterLink>
      </u>
      .
    </div>
  );
};

/**
 * UnignoreIssuesModal represents the UI of the modal for unignoring issues.
 */
const UnignoreIssuesModal: VoidFunctionComponent<UnignoreIssuesModalProps> = ({
  show,
  selectedIssuesCount,
  onHide,
  onUnignore,
  areAnyIgnoredByRule,
}) => {
  const { loading: isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);

  //  Ensure that when the component unmounts, we remove any error messages
  useClearErrorsOnUnmount();

  return (
    <Modal backdrop="static" animation show={show} onHide={onHide} onEscapeKeyDown={onHide} className="issues-modal">
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">Un{ignoreOrResolveWord().toLowerCase()}</Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <span className="issues-modal__body__message">
          {`${selectedIssuesCount} ${pluralizeIssues(
            selectedIssuesCount,
          )} will be un${ignoreOrResolveWordPast().toLowerCase()} and made active.`}
        </span>
        <UnignoreExceptionIssueWarning
          areAnyIgnoredByRule={areAnyIgnoredByRule}
          selectedIssuesCount={selectedIssuesCount}
        />
        {errors && (
          <div className="issues-modal__body__errors">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} isDisabled={isLoading} onClick={onHide}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={onUnignore}
          >
            Un{ignoreOrResolveWord().toLowerCase()}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Container component for the unignore modal for use in the IssuesInbox.
 * For the inbox, after unignoring we need to re-fetch the issues and status summary.
 */
export const UnignoreIssuesModalInboxContainer: VoidFunctionComponent<UnignoreIssuesModalBaseProps> = ({
  show,
  selectedIssuesCount,
  onHide,
  areAnyIgnoredByRule,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const onUnignore = async () => {
    //  We need the action to hit the backend before we re-fetch the status summary.
    await dispatch(IssuesV2Store.actions.unignoreSelectedIssues());
    onHide();
    addToast({
      type: 'success',
      content: `Un${ignoreOrResolveWordPast().toLowerCase()} ${pluralizeIssues(selectedIssuesCount)}`,
    });
  };

  return (
    <UnignoreIssuesModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onUnignore={onUnignore}
      areAnyIgnoredByRule={areAnyIgnoredByRule}
    />
  );
};

type UnignoreIssuesModalDetailsProps = UnignoreIssuesModalBaseProps & {
  id: number;
};

/**
 * Container component for the unignore modal for use in the IssueDetails.
 */
export const UnignoreIssuesModalDetailsContainer: VoidFunctionComponent<UnignoreIssuesModalDetailsProps> = ({
  id,
  show,
  selectedIssuesCount,
  onHide,
  areAnyIgnoredByRule,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();

  const onUnignore = async () => {
    //  We need the action to hit the backend before we re-fetch the status summary.
    await dispatch(IssuesV2Store.actions.unignoreIssues({ issueIds: [id] }));

    //  This handles either closing the drawer (if on the inbox) or
    //  re-fetching the issue details (if we're on the full-page).
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(id));

    onHide();
    addToast({
      type: 'success',
      content: `Un${ignoreOrResolveWordPast().toLowerCase()} ${pluralizeIssues(selectedIssuesCount)}`,
    });
  };

  return (
    <UnignoreIssuesModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onUnignore={onUnignore}
      areAnyIgnoredByRule={areAnyIgnoredByRule}
    />
  );
};
